import React from 'react'
import styled from 'styled-components'
import { useRef } from 'react';
import emailjs from '@emailjs/browser';
import { Snackbar } from '@mui/material';
import { RoughBox, RoughUnderline } from '../common/Rough';
import { ChalkDoodles } from '../common/Doodles';

const Container = styled.div`
display: flex;
flex-direction: column;
justify-content: center;
position: relative;
z-index: 1;
align-items: center;
padding: 0 16px;
@media (max-width: 960px) {
    padding: 0 16px;
}
`

const Wrapper = styled.div`
position: relative;
display: flex;
justify-content: space-between;
align-items: center;
flex-direction: column;
width: 100%;
max-width: 1000px;
padding: 0px 0px 80px 0px;
gap: 12px;
@media (max-width: 960px) {
    flex-direction: column;
}
`

const Title = styled.div`
font-family: 'Caveat', cursive;
font-size: 52px;
text-align: center;
font-weight: 700;
margin-top: 20px;
color: ${({ theme }) => theme.text_primary};
@media (max-width: 768px) {
    margin-top: 12px;
    font-size: 40px;
}
`;

const TitleUnderline = styled.div`
  width: 130px;
  margin: -8px auto 0 auto;
`;

const Desc = styled.div`
    font-family: 'Kalam', cursive;
    font-size: 18px;
    text-align: center;
    max-width: 600px;
    color: ${({ theme }) => theme.text_secondary};
    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 16px;
    }
`;

const ContactFormWrapper = styled.div`
  width: 95%;
  max-width: 600px;
  position: relative;
  margin-top: 28px;
`;

const ContactForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;

const FieldWrap = styled.div`
  width: 100%;
`;

const ContactInput = styled.input`
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  font-family: 'Kalam', cursive;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  padding: 4px 6px;
`

const ContactInputMessage = styled.textarea`
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  font-family: 'Kalam', cursive;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  padding: 4px 6px;
  resize: vertical;
`

const ErrorText = styled.div`
  color: #ff8a8a;
  font-family: 'Kalam', cursive;
  font-size: 14px;
  margin-top: -8px;
  margin-left: 4px;
`;

const ContactButton = styled.input`
  width: 100%;
  text-decoration: none;
  text-align: center;
  border: none;
  cursor: pointer;
  background: transparent;
  color: ${({ theme }) => theme.primary};
  font-family: 'Kalam', cursive;
  font-size: 18px;
  font-weight: 700;
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};
  transition: all 0.2s ease;

  &:hover {
    transform: ${({ disabled }) => disabled ? 'none' : 'rotate(-1deg) scale(1.03)'};
  }
`;



const Contact = () => {

  //hooks
  const [open, setOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const form = useRef();

  const validateForm = () => {
    const formData = new FormData(form.current);
    const email = formData.get('from_email');
    const message = formData.get('message');
    const newErrors = {};

    if (!email || email.trim() === '') {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!message || message.trim() === '') {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSending(true);
    emailjs.sendForm('service_ktcsaua', 'template_sc5um8f', form.current, '1xO2BuVa15hk_nwrY')
      .then((result) => {
        setOpen(true);
        form.current.reset();
        setSending(false);
        setErrors({});
        
        // Reset button text after 3 seconds
        setTimeout(() => {
          setSending(false);
        }, 100);
      }, (error) => {
        console.log(error.text);
        setSending(false);
      });
  }



  return (
    <Container id="contact">
      <ChalkDoodles seedBase={19} count={7} />
      <Wrapper>
        <Title>Connect</Title>
        <TitleUnderline>
          <RoughUnderline height={16} strokeWidth={3.5} />
        </TitleUnderline>
        <Desc>Let's get into touch</Desc>
        <ContactFormWrapper>
          <RoughBox roughness={2.3} strokeWidth={1.8} padding="32px">
            <ContactForm ref={form} onSubmit={handleSubmit}>
              <FieldWrap>
                <RoughBox roughness={2} strokeWidth={1.3} padding="2px 6px">
                  <ContactInput
                    placeholder="Email"
                    name="from_email"
                    error={errors.email}
                    type="email"
                  />
                </RoughBox>
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </FieldWrap>
              <FieldWrap>
                <RoughBox roughness={2} strokeWidth={1.3} padding="2px 6px">
                  <ContactInputMessage
                    placeholder="Message/Feedback"
                    rows="3"
                    name="message"
                    error={errors.message}
                  />
                </RoughBox>
                {errors.message && <ErrorText>{errors.message}</ErrorText>}
              </FieldWrap>
              <RoughBox roughness={2.2} strokeWidth={1.6} padding="12px 16px">
                <ContactButton
                  type="submit"
                  value={sending ? "Message sent ✓" : "Send"}
                  disabled={sending}
                />
              </RoughBox>
            </ContactForm>
          </RoughBox>
        </ContactFormWrapper>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={()=>setOpen(false)}
          message="Email sent successfully!"
          severity="success"
        />
      </Wrapper>
    </Container>
  )
}

export default Contact